import * as yup from 'yup';
import {
	getFormDefinition,
	useGovForm,
} from '@gov-design-system-ce/react-form-core';
import type { CiselnikyVolbyDto } from '@gov-nx/api';
import { useStreetNumbersFormDefinitions } from '@gov-nx/core/hooks';
import { useLocale } from '@gov-nx/core/service';
import { getValues } from '@gov-nx/core/types';
import type { IElectionControl, ServiceCode } from '@gov-nx/module/service';
import {
	getSelectStringDefinition,
	getStringOneOfSchema,
	useInputDefinition,
} from '@gov-nx/utils/common';
import type { FormDataStep3 } from './context.types';
import {
	ZpusobPrevzeti,
	ZpusobPrevzetiUrad,
	ZpusobPrevzetiPosta,
} from './context.types';
import type { IFormStep3, Step3FieldCondition } from './form.types';
import { useVoterRegistrationCardStrings } from './step3StringsHook';

interface FormStep3Props {
	code: ServiceCode;
	embassyOfficesOptions: { value: string; label: string }[];
	checkMutationData?: IElectionControl;
	countryOptions: { value: string; label: string }[];
	election?: CiselnikyVolbyDto;
}

export const FormStep3 = ({
	code,
	embassyOfficesOptions,
	checkMutationData,
	election,
	countryOptions,
}: FormStep3Props): IFormStep3 => {
	const { t } = useLocale(code);

	const jinaNezTrvalaAdresaConditions: Step3FieldCondition[] = [
		{ fieldName: 'zpusobPrevzeti', is: ZpusobPrevzeti.POSTA },
		{
			fieldName: 'zpusobPrevzetiPosta',
			is: ZpusobPrevzetiPosta.JINA_NEZ_TRVALA_ADRESA,
		},
	];

	const streetNumber = useStreetNumbersFormDefinitions({
		name: 'cisloUlice',
		isRequiredWhenAll: { conditions: jinaNezTrvalaAdresaConditions },
	});

	const ulice = useInputDefinition({
		name: 'ulice',
		label: t('formular.pole.nazev-ulice'),
		placeholder: t('formular.placeholder.nazev-ulice'),
		isRequiredWhenAll: {
			conditions: jinaNezTrvalaAdresaConditions,
			errorMessage: t('povinne-pole'),
		},
	});

	const obec = useInputDefinition({
		name: 'obec',
		label: t('formular.pole.nazev-obce'),
		placeholder: t('formular.placeholder.nazev-obce'),
		isRequiredWhenAll: {
			conditions: jinaNezTrvalaAdresaConditions,
			errorMessage: t('povinne-pole'),
		},
	});

	const cislo = useInputDefinition({
		name: 'cislo',
		label: t('formular.pole.cislo'),
		placeholder: t('formular.placeholder.cislo'),
		isRequiredWhenAll: {
			conditions: jinaNezTrvalaAdresaConditions,
			errorMessage: t('povinne-pole'),
		},
	});

	const stat = getSelectStringDefinition({
		name: 'stat',
		label: t('formular.pole.stat'),
		options: countryOptions,
		isRequiredWhenAll: {
			conditions: jinaNezTrvalaAdresaConditions,
			errorMessage: t('povinne-pole'),
		},
	});

	const uradZahranicniConditions: Step3FieldCondition[] = [
		{ fieldName: 'zpusobPrevzeti', is: ZpusobPrevzeti.URAD },
		{
			fieldName: 'zpusobPrevzetiUrad',
			is: ZpusobPrevzetiUrad.URAD_ZAHRANICNI,
		},
	];

	const volitNaOkrskuVZahranici = getSelectStringDefinition({
		name: 'volitNaOkrskuVZahraniciKod',
		label: t('formular.pole.misto-zastupitelskehu-uradu'),
		options: [
			{
				value: '',
				label: t('formular.pole.misto-zastupitelskehu-uradu-zvolte'),
				isDisabled: true,
			},
			...embassyOfficesOptions,
		],
		isRequiredWhenAll: {
			conditions: uradZahranicniConditions,
			errorMessage: t('povinne-pole'),
		},
	});

	const zpusobPrevzeti = getStringOneOfSchema(
		getValues(ZpusobPrevzeti),
		ZpusobPrevzeti.POSTA
	);
	const zpusobPrevzetiPosta = getStringOneOfSchema(
		getValues(ZpusobPrevzetiPosta),
		ZpusobPrevzetiPosta.TRVALA_ADRESA
	);
	const zpusobPrevzetiUrad = getStringOneOfSchema(
		getValues(ZpusobPrevzetiUrad),
		ZpusobPrevzetiUrad.DLE_TRVALEHO_BYDLISTE
	);

	const formSchema = yup
		.object({
			zpusobPrevzeti: zpusobPrevzeti.schema,
			zpusobPrevzetiPosta: zpusobPrevzetiPosta.schema,
			zpusobPrevzetiUrad: zpusobPrevzetiUrad.schema,
			ulice: ulice.schema,
			cisloUlice: streetNumber.schema,
			obec: obec.schema,
			cislo: cislo.schema,
			stat: stat.schema,
			volitNaOkrskuVZahraniciKod: volitNaOkrskuVZahranici.schema,
		})
		.required();

	const formMethods = useGovForm<FormDataStep3>({
		formSchema,
		defaultValues: {
			zpusobPrevzeti: zpusobPrevzeti.defaultValue,
			zpusobPrevzetiPosta: zpusobPrevzetiPosta.defaultValue,
			zpusobPrevzetiUrad: zpusobPrevzetiUrad.defaultValue,
			cisloUlice: streetNumber.defaultValue,
			obec: obec.defaultValue,
			ulice: ulice.defaultValue,
			cislo: cislo.defaultValue,
			stat: stat.defaultValue,
			volitNaOkrskuVZahraniciKod: volitNaOkrskuVZahranici.defaultValue,
		},
	});

	const strings = useVoterRegistrationCardStrings(checkMutationData, election);

	return {
		fields: {
			ulice: ulice.field,
			obec: obec.field,
			cislo: cislo.field,
			stat: stat.field,
			volitNaOkrskuVZahranici: volitNaOkrskuVZahranici.field,
			cisloUlice: streetNumber.field,
		},
		formDefinition: getFormDefinition({ formMethods, formSchema }),
		strings,
	};
};
